import React, { useState, useMemo, useCallback } from "react";
import { Form, Select, Button, Card, Space, message, Input, Divider, Alert, Radio, DatePicker } from "antd";
import { SaveOutlined, ReloadOutlined } from "@ant-design/icons";
import AircraftSelector from "../../../components/common/AircraftSelector";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { flightOperationFlightService } from "../../../api/enhancedApi";

dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;
const { TextArea } = Input;

const PLANNING_STATUS = {
    CONFIRMED: "CONFIRMED",
    PENDING: "PENDING",
};

const EVENT_TYPE_OPTIONS = [
    { value: "SCHEDULED", label: "定检维护" },
    { value: "ROUTINE", label: "例行维护" },
    { value: "AD_HOC", label: "临时维护" },
];

/**
 * 定检维护事件录入表单组件
 * 用于录入飞机定检维护事件，采用双锚点定位（前后航班）
 */
const MaintenanceEventForm = ({ onSuccess, loading: externalLoading }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [flightsLoading, setFlightsLoading] = useState(false);
    const [flights, setFlights] = useState([]);
    const [selectedAircraft, setSelectedAircraft] = useState(null);
    const [planningStatus, setPlanningStatus] = useState(PLANNING_STATUS.CONFIRMED);
    const [prevFlightIndex, setPrevFlightIndex] = useState(-1);
    const [prevFlightInfo, setPrevFlightInfo] = useState(null);
    const [nextFlightInfo, setNextFlightInfo] = useState(null);

    // 生成航班的唯一key
    const getFlightKey = (flight) => {
        const flightDate = dayjs(flight.flight_date).format("YYYY-MM-DD");
        return `${flight.id}|${flight.flight_number}|${flightDate}`;
    };

    // 当选择飞机后，加载该飞机未来30天的航班
    const loadFlights = useCallback(async (aircraftReg) => {
        if (!aircraftReg) {
            setFlights([]);
            return;
        }

        try {
            setFlightsLoading(true);
            const startDate = dayjs().subtract(7, "day").format("YYYY-MM-DD");
            const endDate = dayjs().add(30, "day").format("YYYY-MM-DD");

            // 调用航班查询API
            const response = await flightOperationFlightService.getFlightOperations({
                aircraft_reg: aircraftReg,
                start_date: startDate,
                end_date: endDate,
                page_size: 200,
            });

            const EXCLUDED_STATUS = ['CANCELLED', 'PLAN_CANCELLED', 'REPLACED'];
            const flightList = (response.data?.items || []).filter(
                f => !EXCLUDED_STATUS.includes(f.status)
            );
            flightList.sort((a, b) => dayjs(a.std).diff(dayjs(b.std)));
            setFlights(flightList);
        } catch (error) {
            console.error("加载航班列表失败:", error);
            message.error("加载航班列表失败");
            setFlights([]);
        } finally {
            setFlightsLoading(false);
        }
    }, []);

    const handlePlanningStatusChange = (e) => {
        const status = e.target.value;
        setPlanningStatus(status);
        form.setFieldsValue({
            prev_flight_key: undefined,
            next_flight_key: undefined,
            expected_start_date: undefined,
        });
        setPrevFlightIndex(-1);
        setPrevFlightInfo(null);
        setNextFlightInfo(null);

        if (status === PLANNING_STATUS.CONFIRMED && selectedAircraft) {
            loadFlights(selectedAircraft);
        }
    };

    const handleAircraftChange = (value) => {
        setSelectedAircraft(value);
        form.setFieldsValue({
            prev_flight_key: undefined,
            next_flight_key: undefined,
        });
        setPrevFlightIndex(-1);
        setPrevFlightInfo(null);
        setNextFlightInfo(null);
        
        if (value && planningStatus === PLANNING_STATUS.CONFIRMED) {
            loadFlights(value);
        } else {
            setFlights([]);
        }
    };

    // 处理前一航班选择变化 - 自动选择下一个航班
    const handlePrevFlightChange = (value) => {
        if (!value) {
            setPrevFlightIndex(-1);
            setPrevFlightInfo(null);
            setNextFlightInfo(null);
            form.setFieldsValue({ next_flight_key: undefined });
            return;
        }
        
        // value 格式: "id|航班号|日期"
        const [flightId, flightNumber, flightDate] = value.split("|");
        const flightIndex = flights.findIndex(f => String(f.id) === flightId);
        const flight = flights[flightIndex];
        
        if (flight) {
            setPrevFlightIndex(flightIndex);
            setPrevFlightInfo({
                flight_number: flightNumber,
                flight_date: flightDate,
                sta: flight.sta ? dayjs(flight.sta).tz("Asia/Shanghai").format("MM-DD HH:mm") : "--:--",
            });
            
            // 自动选择下一个航班
            if (flightIndex < flights.length - 1) {
                const nextFlight = flights[flightIndex + 1];
                const nextKey = getFlightKey(nextFlight);
                const nextFlightDate = dayjs(nextFlight.flight_date).format("YYYY-MM-DD");
                
                form.setFieldsValue({ next_flight_key: nextKey });
                setNextFlightInfo({
                    flight_number: nextFlight.flight_number,
                    flight_date: nextFlightDate,
                    std: nextFlight.std ? dayjs(nextFlight.std).tz("Asia/Shanghai").format("MM-DD HH:mm") : "--:--",
                });
            } else {
                // 没有下一个航班
                form.setFieldsValue({ next_flight_key: undefined });
                setNextFlightInfo(null);
            }
        }
    };

    // 处理后一航班选择变化
    const handleNextFlightChange = (value) => {
        if (!value) {
            setNextFlightInfo(null);
            return;
        }
        
        // value 格式: "id|航班号|日期"
        const [flightId, flightNumber, flightDate] = value.split("|");
        const flight = flights.find(f => String(f.id) === flightId);
        
        if (flight) {
            setNextFlightInfo({
                flight_number: flightNumber,
                flight_date: flightDate,
                std: flight.std ? dayjs(flight.std).tz("Asia/Shanghai").format("MM-DD HH:mm") : "--:--",
            });
        }
    };

    // 后一航班的可选列表（只显示前一航班之后的航班）
    const nextFlightOptions = useMemo(() => {
        if (prevFlightIndex < 0) return [];
        return flights.slice(prevFlightIndex + 1);
    }, [flights, prevFlightIndex]);

    // 根据选择的航班 key 获取航班信息
    const getFlightById = (flightKey) => {
        if (!flightKey) return null;
        const [flightId] = flightKey.split("|");
        return flights.find(f => String(f.id) === flightId);
    };

    // 计算维护时长
    const calculateDuration = () => {
        const prevFlightKey = form.getFieldValue("prev_flight_key");
        const nextFlightKey = form.getFieldValue("next_flight_key");
        
        const prevFlight = getFlightById(prevFlightKey);
        const nextFlight = getFlightById(nextFlightKey);
        
        if (prevFlight?.sta && nextFlight?.std) {
            const diffHours = dayjs(nextFlight.std).diff(dayjs(prevFlight.sta), "hour", true);
            return diffHours > 0 ? diffHours.toFixed(1) : null;
        }
        return null;
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            const formattedData = {
                aircraft_registration: values.aircraft_registration,
                planning_status: planningStatus,
                event_type: values.event_type || "SCHEDULED",
                title: values.title || null,
                description: values.description || null,
            };

            if (planningStatus === PLANNING_STATUS.CONFIRMED) {
                const [, prevFlightNumber, prevFlightDate] = values.prev_flight_key.split("|");
                const [, nextFlightNumber, nextFlightDate] = values.next_flight_key.split("|");
                formattedData.prev_flight_number = prevFlightNumber;
                formattedData.prev_flight_date = prevFlightDate;
                formattedData.next_flight_number = nextFlightNumber;
                formattedData.next_flight_date = nextFlightDate;
            } else {
                formattedData.expected_start_date = values.expected_start_date
                    ? values.expected_start_date.format("YYYY-MM-DD")
                    : null;
            }

            if (onSuccess) {
                await onSuccess(formattedData);
            }

            form.resetFields();
            setSelectedAircraft(null);
            setPlanningStatus(PLANNING_STATUS.CONFIRMED);
            setFlights([]);
            setPrevFlightIndex(-1);
            setPrevFlightInfo(null);
            setNextFlightInfo(null);
        } catch (error) {
            if (error.errorFields) {
                message.error("请检查表单填写是否完整");
            } else {
                console.error("保存维护事件失败:", error);
                message.error(error.message || "保存失败，请重试");
            }
        }
    };

    const handleReset = () => {
        form.resetFields();
        setSelectedAircraft(null);
        setPlanningStatus(PLANNING_STATUS.CONFIRMED);
        setFlights([]);
        setPrevFlightIndex(-1);
        setPrevFlightInfo(null);
        setNextFlightInfo(null);
        message.info("表单已重置");
    };

    // 生成航班选项（使用ID作为唯一标识，时间显示北京时间）
    const renderFlightOption = (flight) => {
        const flightDate = dayjs(flight.flight_date).format("YYYY-MM-DD");
        const stdStr = flight.std ? dayjs(flight.std).tz("Asia/Shanghai").format("MM-DD HH:mm") : "--";
        const staStr = flight.sta ? dayjs(flight.sta).tz("Asia/Shanghai").format("MM-DD HH:mm") : "--";
        const key = `${flight.id}|${flight.flight_number}|${flightDate}`;
        
        return (
            <Option key={key} value={key}>
                {flight.flight_number} | {flight.departure_airport}→{flight.arrival_airport} | {stdStr} → {staStr}
            </Option>
        );
    };

    // 前一航班选项（所有航班，除了最后一个）
    const renderPrevFlightOptions = () => {
        // 最后一个航班不能作为"前一航班"（因为没有后续航班）
        return flights.slice(0, -1).map(renderFlightOption);
    };

    // 后一航班选项（只显示前一航班之后的航班）
    const renderNextFlightOptions = () => {
        return nextFlightOptions.map(renderFlightOption);
    };

    const duration = calculateDuration();

    const isConfirmed = planningStatus === PLANNING_STATUS.CONFIRMED;

    return (
        <Card title="定检维护事件录入" size="small">
            <Form
                form={form}
                layout="vertical"
                autoComplete="off"
                initialValues={{ event_type: "SCHEDULED" }}
            >
                <Form.Item label="计划状态">
                    <Radio.Group
                        value={planningStatus}
                        onChange={handlePlanningStatusChange}
                        buttonStyle="solid"
                        optionType="button"
                        size="small"
                    >
                        <Radio.Button value={PLANNING_STATUS.CONFIRMED}>计划已确定</Radio.Button>
                        <Radio.Button value={PLANNING_STATUS.PENDING}>计划待定</Radio.Button>
                    </Radio.Group>
                </Form.Item>

                {!isConfirmed && (
                    <Alert
                        message="计划待定模式：仅需填写飞机和预计日期，航班计划确定后可再编辑补充"
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form.Item
                    name="aircraft_registration"
                    label="飞机注册号"
                    rules={[
                        { required: true, message: "请选择飞机注册号" },
                    ]}
                >
                    <AircraftSelector
                        allowClear
                        placeholder="选择或搜索飞机注册号"
                        valueType="registration"
                        displayMode="simple"
                        style={{ width: "100%" }}
                        onChange={handleAircraftChange}
                    />
                </Form.Item>

                {isConfirmed ? (
                    <>
                        <Divider orientation="left" plain style={{ margin: "12px 0" }}>
                            维护区间 <span style={{ fontSize: 12, color: "#999" }}>（时间均为 UTC+8）</span>
                        </Divider>

                        <Form.Item
                            name="prev_flight_key"
                            label="前一航班（维护开始锚点）"
                            rules={[
                                { required: true, message: "请选择前一航班" },
                            ]}
                            extra={prevFlightInfo ? `落地时间 (UTC+8): ${prevFlightInfo.sta}` : null}
                        >
                            <Select
                                showSearch
                                placeholder={selectedAircraft ? "选择前一航班（自动关联后一航班）" : "请先选择飞机"}
                                loading={flightsLoading}
                                disabled={!selectedAircraft || flights.length < 2}
                                onChange={handlePrevFlightChange}
                                filterOption={(input, option) =>
                                    option.children?.toString().toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {renderPrevFlightOptions()}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="next_flight_key"
                            label="后一航班（维护结束锚点）"
                            rules={[
                                { required: true, message: "请选择后一航班" },
                            ]}
                            extra={nextFlightInfo ? `起飞时间 (UTC+8): ${nextFlightInfo.std}` : null}
                        >
                            <Select
                                showSearch
                                placeholder={prevFlightIndex >= 0 ? "可调整后一航班" : "请先选择前一航班"}
                                loading={flightsLoading}
                                disabled={prevFlightIndex < 0}
                                onChange={handleNextFlightChange}
                                filterOption={(input, option) =>
                                    option.children?.toString().toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {renderNextFlightOptions()}
                            </Select>
                        </Form.Item>

                        {duration && (
                            <Alert
                                message={`自动计算维护时长: ${duration} 小时`}
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}
                    </>
                ) : (
                    <Form.Item
                        name="expected_start_date"
                        label="预计维护日期"
                        rules={[
                            { required: true, message: "请选择预计维护日期" },
                        ]}
                    >
                        <DatePicker
                            placeholder="选择预计开始日期"
                            style={{ width: "100%" }}
                            format="YYYY-MM-DD"
                        />
                    </Form.Item>
                )}

                <Divider style={{ margin: "12px 0" }} />

                <Form.Item
                    name="event_type"
                    label="维护类型"
                >
                    <Select placeholder="选择维护类型">
                        {EVENT_TYPE_OPTIONS.map(opt => (
                            <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="title"
                    label="显示标题"
                    initialValue="定检维护"
                >
                    <Input placeholder="例如：定检维护 (24h)" maxLength={100} />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="备注（可选）"
                >
                    <TextArea rows={2} placeholder="备注信息" maxLength={500} />
                </Form.Item>

                <Form.Item>
                    <Space>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={handleSubmit}
                            loading={loading || externalLoading}
                        >
                            添加事件
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleReset}
                        >
                            重置
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default MaintenanceEventForm;
